import React, { useMemo, useRef, useState } from 'react';
import { Button } from '@attentive-platform/stem-ui';

import BrandMark from './BrandMark';
import ScopeItem from './ScopeItem';
import SavedLabel from './SavedLabel';
import ConfirmModal from './ConfirmModal';
import SearchOmnibox from './SearchOmnibox';
import TradeDetailingBlock from './TradeDetailingBlock';
import { FormScreenStyles as S } from './styles';

const STATE_BANNER = {
  SUBMITTED: {
    tag: 'Submitted',
    title: 'You’ve already submitted this scope',
    body: 'Ask your account team to reopen the link if something needs to change.'
  },
  REOPENED: {
    tag: 'Reopened',
    title: 'Open for edits',
    body: 'Revise anything and resubmit — your previous answers are preserved.'
  }
};

const groupSections = trade => {
  const sections = new Map();
  (trade.scopes || []).forEach(sc => {
    const elements = sc.elements || [];
    if (!elements.length) return;
    const key = sc.section_id || sc.section_name || 'section';
    const name = sc.section_name || 'Items';
    if (!sections.has(key)) sections.set(key, { id: key, name, items: [] });
    sections.get(key).items.push(sc);
  });
  return Array.from(sections.values()).filter(sec => sec.items.length > 0);
};

const FormScreen = ({
  trades,
  activeTradeIdx,
  onSelectTrade,
  draft,
  savedAt,
  onToggleScope,
  onToggleElement,
  onModifierChange,
  onNote,
  onClearTrade,
  onReview,
  readOnly,
  state,
  reopenReason,
  headerTitle
}) => {
  const trade = trades[activeTradeIdx];
  const sections = useMemo(() => (trade ? groupSections(trade) : []), [trade]);
  const sectionRefs = useRef({});
  const scopeRefs = useRef({});
  const [reviewCta, setReviewCta] = useState(false);
  const [clearConfirmTrade, setClearConfirmTrade] = useState(null);

  const handleSearchPick = row => {
    if (row.tradeIdx !== activeTradeIdx) onSelectTrade(row.tradeIdx);
    setTimeout(() => {
      const el = scopeRefs.current[row.scopeId];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  const pickedInTrade = useMemo(() => {
    return sections.reduce(
      (a, sec) => a + sec.items.filter(sc => draft.scopes[sc.scope_id]).length,
      0
    );
  }, [sections, draft.scopes]);

  const pickedTotal = useMemo(
    () =>
      trades.reduce(
        (a, t) =>
          a +
          (t.scopes || []).filter(
            sc => (sc.elements || []).length > 0 && draft.scopes[sc.scope_id]
          ).length,
        0
      ),
    [trades, draft.scopes]
  );
  const nextTrade = trades[activeTradeIdx + 1];

  const banner = STATE_BANNER[state];
  const bannerBody = state === 'REOPENED' && reopenReason ? reopenReason : banner?.body;
  const noteValue = draft.notes[trade?.trade_id];

  const scrollToSection = id => {
    const el = sectionRefs.current[id];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const tradeMods = (trade?.trade_modifiers || []).filter(
    m =>
      m &&
      (m.modifier_type === 'example' ||
        m.title ||
        m.body ||
        (m.answer_options || []).length ||
        (Array.isArray(m.urls) && m.urls.length > 0))
  );
  const modifierAnswers = draft.modifiers;

  const BannerWrap = state === 'SUBMITTED' ? S.BannerSubmitted : S.BannerReopened;
  const BannerTag = state === 'SUBMITTED' ? S.BannerTagSubmitted : S.BannerTagReopened;

  return (
    <S.PageWrap>
      {banner && (
        <BannerWrap>
          <S.BannerInner>
            <BannerTag>{banner.tag}</BannerTag>
            <S.BannerTitle>{banner.title}</S.BannerTitle>
            <S.BannerBody>{bannerBody}</S.BannerBody>
          </S.BannerInner>
        </BannerWrap>
      )}

      <S.TopBar>
        <S.TopBarInner>
          <S.BrandRow>
            <BrandMark compact />
            <S.Divider />
            <S.HeaderTitle title={headerTitle}>{headerTitle}</S.HeaderTitle>
          </S.BrandRow>
          <S.TopBarRight>
            <SearchOmnibox trades={trades} draft={draft} onPick={handleSearchPick} />
            <S.PickedLabel>
              {pickedTotal} item{pickedTotal === 1 ? '' : 's'} picked
            </S.PickedLabel>
            {!readOnly && <SavedLabel savedAt={savedAt} />}
            {readOnly ? (
              <Button variant='text' color='primary' size='large' onClick={onReview}>
                View summary
              </Button>
            ) : (
              <Button
                variant='outlined'
                color='primary'
                size='large'
                onMouseEnter={() => setReviewCta(true)}
                onMouseLeave={() => setReviewCta(false)}
                onClick={onReview}>
                {reviewCta && pickedTotal === 0 ? 'Nothing picked yet' : 'Review & submit'}
              </Button>
            )}
          </S.TopBarRight>
        </S.TopBarInner>
      </S.TopBar>

      <ConfirmModal
        open={!!clearConfirmTrade}
        title={`Clear everything in ${clearConfirmTrade?.trade_name || 'this trade'}?`}
        body='Ticked items, element picks, question answers and the trade note all reset. This affects this trade only.'
        confirmLabel='Clear trade'
        confirmVariant='error'
        onConfirm={() => {
          if (clearConfirmTrade) onClearTrade(clearConfirmTrade);
          setClearConfirmTrade(null);
        }}
        onCancel={() => setClearConfirmTrade(null)}
      />

      {trade && (
        <S.Layout>
          <S.Rail>
            <S.RailHeading>
              All trades · {trades.length}
            </S.RailHeading>
            {trades.map((t, idx) => {
              const isActive = idx === activeTradeIdx;
              const tradeCount = (t.scopes || []).filter(
                sc => (sc.elements || []).length > 0 && draft.scopes[sc.scope_id]
              ).length;
              const Dot = tradeCount > 0 ? S.RailDotOn : S.RailDotOff;
              return (
                <S.RailTradeWrap key={t.trade_id}>
                  <S.RailTradeRow active={isActive} onClick={() => onSelectTrade(idx)}>
                    <Dot />
                    <S.RailTradeName active={isActive}>{t.trade_name}</S.RailTradeName>
                    {tradeCount > 0 && <S.RailCount>{tradeCount}</S.RailCount>}
                  </S.RailTradeRow>
                  {isActive && sections.length > 0 && (
                    <S.RailSectionList>
                      {sections.map(s => {
                        const count = s.items.filter(sc => draft.scopes[sc.scope_id]).length;
                        const SDot = count > 0 ? S.RailDotOn : S.RailDotOff;
                        return (
                          <S.RailSectionRow key={s.id} onClick={() => scrollToSection(s.id)}>
                            <SDot />
                            <S.RailName>{s.name}</S.RailName>
                            <S.RailSectionCount>
                              {count > 0 ? `${count}/${s.items.length}` : s.items.length}
                            </S.RailSectionCount>
                          </S.RailSectionRow>
                        );
                      })}
                    </S.RailSectionList>
                  )}
                </S.RailTradeWrap>
              );
            })}
          </S.Rail>

          <S.Main>
            <S.IntroCard>
              <S.IntroHead>
                <S.IntroTitle>{trade.trade_name}</S.IntroTitle>
                {!readOnly && pickedInTrade > 0 && (
                  <S.ClearTradeBtn onClick={() => setClearConfirmTrade(trade)}>Clear this trade</S.ClearTradeBtn>
                )}
              </S.IntroHead>
              {trade.trade_description && <S.IntroDesc>{trade.trade_description}</S.IntroDesc>}
              <S.IntroHint>Skip anything you don&apos;t bid — leave those items un-ticked.</S.IntroHint>
            </S.IntroCard>

            {sections.map(sec => (
              <S.SectionBlock
                key={sec.id}
                ref={el => {
                  sectionRefs.current[sec.id] = el;
                }}>
                <S.SectionHeaderRow>
                  <S.SectionName>{sec.name}</S.SectionName>
                  <S.SectionHR />
                  <S.SectionCount>
                    {sec.items.filter(sc => draft.scopes[sc.scope_id]).length} of {sec.items.length}
                  </S.SectionCount>
                </S.SectionHeaderRow>
                {sec.items.map(sc => (
                  <div
                    key={sc.scope_id}
                    ref={el => {
                      scopeRefs.current[sc.scope_id] = el;
                    }}>
                    <ScopeItem
                      scope={sc}
                      checked={!!draft.scopes[sc.scope_id]}
                      elementsOn={draft.elements}
                      modifierAnswers={modifierAnswers}
                      onToggleScope={onToggleScope}
                      onToggleElement={onToggleElement}
                      onModifierChange={onModifierChange}
                      readOnly={readOnly}
                    />
                  </div>
                ))}
              </S.SectionBlock>
            ))}

            {(tradeMods.length > 0 || !readOnly) && (
              <TradeDetailingBlock
                trade={{ ...trade, trade_modifiers: tradeMods }}
                modifierAnswers={modifierAnswers}
                onModifierChange={onModifierChange}
                noteValue={noteValue}
                onNoteChange={value => onNote(trade.trade_id, value)}
                readOnly={readOnly}
              />
            )}

            <S.Footer>
              <S.FooterHint>
                {pickedInTrade > 0
                  ? `${pickedInTrade} item${pickedInTrade === 1 ? '' : 's'} picked in ${trade.trade_name}`
                  : `Nothing picked in ${trade.trade_name} yet`}
              </S.FooterHint>
              <S.FooterActions>
                {nextTrade ? (
                  <Button
                    variant={pickedInTrade > 0 ? 'contained' : 'outlined'}
                    color='primary'
                    size='large'
                    onClick={() => onSelectTrade(activeTradeIdx + 1)}>
                    Next: {nextTrade.trade_name}
                  </Button>
                ) : (
                  !readOnly && (
                    <Button variant='contained' color='primary' size='large' onClick={onReview}>
                      Review &amp; submit
                    </Button>
                  )
                )}
              </S.FooterActions>
            </S.Footer>
          </S.Main>
        </S.Layout>
      )}
    </S.PageWrap>
  );
};

export default FormScreen;
